import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Bot, Play, Zap, AlertTriangle, Sparkles, TerminalSquare, RefreshCw } from "lucide-react";

const IMPACT_COLOR = { revenue: "text-gold", growth: "text-lawn", retention: "text-blue-400", conversion: "text-purple-400" };
const PRIORITY_COLOR = { high: "bg-red-500/20 text-red-300", medium: "bg-gold/20 text-gold", low: "bg-white/10 text-white/50" };

export default function AICommandCenter() {
  const [agents, setAgents] = useState([]);
  const [recs, setRecs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [running, setRunning] = useState("");
  const [genKind, setGenKind] = useState("social");
  const [genTopic, setGenTopic] = useState("");
  const [genResult, setGenResult] = useState("");
  const [genLoading, setGenLoading] = useState(false);

  const load = () => {
    api.get("/ai/agents").then((r) => setAgents(r.data.agents)).catch(() => {});
    api.get("/ai/recommendations").then((r) => setRecs(r.data.recommendations)).catch(() => {});
    api.get("/ai/logs").then((r) => setLogs(r.data.logs)).catch(() => {});
  };
  useEffect(load, []);

  const runAgent = async (key) => {
    setRunning(key);
    try { await api.post("/ai/agents/run", { agent_key: key }); toast.success("Agent completed its analysis"); load(); }
    catch { toast.error("Agent run failed"); } finally { setRunning(""); }
  };

  const runAll = async () => {
    setRunning("all");
    toast.info("Deploying the AI Agent Army… this may take a moment.");
    try { await api.post("/ai/agents/run-all"); toast.success("All agents completed"); load(); }
    catch { toast.error("Run failed"); } finally { setRunning(""); }
  };

  const generate = async () => {
    if (!genTopic.trim()) { toast.error("Enter a topic"); return; }
    setGenLoading(true); setGenResult("");
    try { const r = await api.post("/ai/generate", { kind: genKind, topic: genTopic }); setGenResult(r.data.content); }
    catch { toast.error("Generation failed"); } finally { setGenLoading(false); }
  };

  const alerts = agents.filter((a) => a.alert);

  return (
    <div className="bg-[#0A0F0D] rounded-2xl p-6 sm:p-8 text-white -m-2" data-testid="ai-command-center">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2 text-lawn"><TerminalSquare className="w-5 h-5" /><span className="text-xs font-bold uppercase tracking-[0.2em] terminal">AI Command Center</span></div>
          <h2 className="font-display text-3xl font-extrabold mt-2">The AI Agent Army</h2>
          <p className="text-white/50 text-sm mt-1">10 autonomous agents analyzing real platform data to drive profitability.</p>
        </div>
        <Button data-testid="run-all-agents" onClick={runAll} disabled={!!running} className="rounded-full bg-lawn hover:bg-lawn-dark font-semibold gap-2">
          {running === "all" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />} Deploy All Agents
        </Button>
      </div>

      {alerts.length > 0 && (
        <div className="mb-6 space-y-2">
          {alerts.map((a) => (
            <div key={a.key} className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-sm">
              <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 shrink-0" /><span><span className="font-semibold text-red-300">{a.name}: </span><span className="text-white/70">{a.alert}</span></span>
            </div>
          ))}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-3">
        {agents.map((a) => (
          <div key={a.key} data-testid={`agent-${a.key}`} className="bg-white/5 border border-white/10 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-lawn/20 flex items-center justify-center shrink-0"><Bot className="w-5 h-5 text-lawn" /></div>
                <div>
                  <p className="font-display font-bold text-sm">{a.name}</p>
                  <p className="text-[11px] text-white/40 leading-snug mt-0.5">{a.purpose}</p>
                </div>
              </div>
              <Button data-testid={`run-agent-${a.key}`} onClick={() => runAgent(a.key)} disabled={!!running} size="sm" variant="ghost" className="text-lawn hover:text-white hover:bg-lawn/20 shrink-0 h-8 gap-1">
                {running === a.key ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              </Button>
            </div>
            {a.last_summary && <p className="text-xs text-white/60 mt-3 terminal bg-black/30 rounded-lg p-2 leading-relaxed">{a.last_summary}</p>}
            <div className="flex items-center gap-3 mt-2 text-[11px] text-white/40">
              <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-lawn animate-pulse" /> Active</span>
              {a.open_recommendations > 0 && <span className="text-gold">{a.open_recommendations} recommendations</span>}
              {a.last_run && <span>· ran {new Date(a.last_run).toLocaleTimeString()}</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Recommendations */}
      <div className="mt-8">
        <h3 className="font-display text-xl font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-gold" /> Agent Recommendations</h3>
        <div className="mt-3 space-y-2 max-h-96 overflow-y-auto pr-1">
          {recs.length === 0 && <p className="text-white/40 text-sm terminal">// No recommendations yet. Deploy the agents to generate insights tied to your data.</p>}
          {recs.map((r) => (
            <div key={r.rec_id} data-testid={`rec-${r.rec_id}`} className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-sm">{r.title}</p>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${PRIORITY_COLOR[r.priority]}`}>{r.priority}</span>
                  <span className={`text-[10px] font-semibold ${IMPACT_COLOR[r.impact] || "text-white/50"}`}>{r.impact}</span>
                </div>
              </div>
              <p className="text-xs text-white/60 mt-1">{r.detail}</p>
              <p className="text-[10px] text-white/30 mt-1">— {r.agent_name}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Content generator */}
      <div className="mt-8 bg-white/5 border border-white/10 rounded-xl p-5">
        <h3 className="font-display text-xl font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-gold" /> AI Content Generator</h3>
        <p className="text-white/50 text-sm mt-1">Generate marketing content with the AI Marketing Expert.</p>
        <div className="flex flex-col sm:flex-row gap-2 mt-4">
          <Select value={genKind} onValueChange={setGenKind}>
            <SelectTrigger data-testid="gen-kind" className="sm:w-48 bg-white/10 border-white/20 text-white rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="social">Social posts</SelectItem>
              <SelectItem value="email">Email campaign</SelectItem>
              <SelectItem value="sms">SMS campaign</SelectItem>
              <SelectItem value="blog">SEO blog post</SelectItem>
              <SelectItem value="sales_script">Sales script</SelectItem>
            </SelectContent>
          </Select>
          <Input data-testid="gen-topic" value={genTopic} onChange={(e) => setGenTopic(e.target.value)} placeholder="e.g. Spring cleanup promo in Austin" className="flex-1 bg-white/10 border-white/20 text-white rounded-xl" />
          <Button data-testid="gen-btn" onClick={generate} disabled={genLoading} className="rounded-xl bg-gold hover:bg-gold-dark text-white font-semibold gap-2">{genLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate</Button>
        </div>
        {genResult && <div data-testid="gen-result" className="mt-4 bg-black/40 rounded-xl p-4 text-sm text-white/80 whitespace-pre-line terminal max-h-72 overflow-y-auto">{genResult}</div>}
      </div>
    </div>
  );
}
